/**
 * Registro de entradas para o widget Sumário (table-of-contents).
 * Varredura na ordem do documento (linhas → colunas → widgets → nestedRows).
 */
import { sanitizeTitleBasicAnchorId } from '../constants/widgetHtmlSnippets';

/** Tipos elegíveis como fonte do sumário. */
export const TOC_SOURCE_TYPES = new Set(['title-basic', 'section-header', 'styled-card']);

/**
 * @param {Record<string, unknown>|null|undefined} content
 * @returns {boolean}
 */
export function isIncludedInToc(content) {
  return content?.includeInToc === true;
}

/**
 * @param {Record<string, unknown>|null|undefined} content
 * @param {string} sourceType
 * @returns {string}
 */
export function resolveTocLabel(content, sourceType) {
  const c = content || {};
  const custom = String(c.tocLabel || '').trim();
  if (custom) return custom;
  if (sourceType === 'title-basic' || sourceType === 'section-header' || sourceType === 'styled-card') {
    return String(c.title || '').trim();
  }
  return '';
}

/**
 * @param {Record<string, unknown>|null|undefined} content
 * @returns {string}
 */
export function resolveTocIcon(content) {
  return String(content?.tocIcon || '').trim();
}

/**
 * @param {Record<string, unknown>|null|undefined} content
 * @param {number} [fallback=1]
 * @returns {1|2|3}
 */
export function resolveTocLevel(content, fallback = 1) {
  const n = parseInt(content?.tocLevel, 10);
  if (Number.isFinite(n) && n >= 1 && n <= 3) return /** @type {1|2|3} */ (n);
  const fb = parseInt(fallback, 10);
  if (Number.isFinite(fb) && fb >= 1 && fb <= 3) return /** @type {1|2|3} */ (fb);
  return 1;
}

/**
 * @param {Record<string, unknown>|null|undefined} content
 * @param {string} widgetId
 * @returns {string}
 */
export function resolveTocAnchorId(content, widgetId) {
  return sanitizeTitleBasicAnchorId(content?.anchorId, widgetId);
}

/**
 * @typedef {object} TocRegistryEntry
 * @property {string} widgetId
 * @property {string} sourceType
 * @property {string} anchorId
 * @property {string} label
 * @property {string} tocIcon
 * @property {1|2|3} level
 * @property {number} order
 * @property {number|null} [pageIndex]
 * @property {number|null} [pageNumber]
 */

/**
 * @param {Array<object>|null|undefined} rows
 * @returns {boolean}
 */
export function courseHasPagination(rows) {
  const visitCol = (col) => {
    for (const w of col.widgets || []) {
      if (w?.type === 'page-break') return true;
    }
    for (const nr of col.nestedRows || []) {
      for (const c2 of nr.columns || []) {
        if (visitCol(c2)) return true;
      }
    }
    return false;
  };
  for (const row of rows || []) {
    for (const col of row.columns || []) {
      if (visitCol(col)) return true;
    }
  }
  return false;
}

/**
 * Replica o pré-processamento de page-break do export (processedRows).
 * @param {Array<object>|null|undefined} rows
 * @returns {Array<object>}
 */
export function buildProcessedRowsForPagination(rows) {
  /** @type {Array<object>} */
  const processedRows = [];
  for (const r of rows || []) {
    let hasPageBreak = false;
    for (const col of r.columns || []) {
      for (const w of col.widgets || []) {
        if (w?.type === 'page-break') {
          hasPageBreak = true;
          break;
        }
      }
      if (hasPageBreak) break;
    }

    if (!hasPageBreak) {
      processedRows.push(r);
      continue;
    }

    const rBefore = { ...r, columns: (r.columns || []).map((c) => ({ ...c, widgets: [] })) };
    const rAfter = { ...r, columns: (r.columns || []).map((c) => ({ ...c, widgets: [] })) };
    let foundAnyBreakInRow = false;

    for (let cIdx = 0; cIdx < (r.columns || []).length; cIdx++) {
      const col = r.columns[cIdx];
      let foundBreakInThisCol = false;
      for (const w of col.widgets || []) {
        if (w.type === 'page-break') {
          foundBreakInThisCol = true;
          foundAnyBreakInRow = true;
        } else if (foundBreakInThisCol || foundAnyBreakInRow) {
          rAfter.columns[cIdx].widgets.push(w);
        } else {
          rBefore.columns[cIdx].widgets.push(w);
        }
      }
    }

    const hasBeforeWidgets = rBefore.columns.some((c) => (c.widgets || []).length > 0);
    if (hasBeforeWidgets) processedRows.push(rBefore);

    const hasAfterWidgets = rAfter.columns.some((c) => (c.widgets || []).length > 0);
    processedRows.push({ isMarker: true, noContentAfter: !hasAfterWidgets });

    if (hasAfterWidgets) processedRows.push(rAfter);
  }
  return processedRows;
}

/**
 * Simula a paginação do export: Map widgetId → pageIndex (0-based).
 * @param {Array<object>|null|undefined} rows
 * @returns {Map<string, number>}
 */
export function computeTocPageIndexByWidgetId(rows) {
  /** @type {Map<string, number>} */
  const map = new Map();
  let completedPages = 0;
  let currentPageHasContent = false;

  const assignRowWidgets = (row, pageIdx) => {
    const visitCol = (col) => {
      for (const w of col.widgets || []) {
        if (!w || w.type === 'page-break') continue;
        map.set(w.id, pageIdx);
      }
      for (const nr of col.nestedRows || []) {
        for (const c2 of nr.columns || []) visitCol(c2);
      }
    };
    for (const col of row.columns || []) visitCol(col);
  };

  for (const r of buildProcessedRowsForPagination(rows)) {
    if (r.isMarker) {
      if (currentPageHasContent) {
        completedPages += 1;
        currentPageHasContent = false;
      }
      continue;
    }
    assignRowWidgets(r, completedPages);
    currentPageHasContent = true;
  }

  return map;
}

/**
 * Coleta entradas marcadas com includeInToc na ordem de leitura do palco.
 * @param {Array<object>|null|undefined} rows
 * @param {{ excludeWidgetId?: string }} [opts]
 * @returns {TocRegistryEntry[]}
 */
export function collectTocEntriesFromRows(rows, opts = {}) {
  const excludeWidgetId = opts.excludeWidgetId || '';
  const hasPagination = courseHasPagination(rows);
  const pageMap = hasPagination ? computeTocPageIndexByWidgetId(rows) : null;
  /** @type {TocRegistryEntry[]} */
  const out = [];
  let order = 0;

  const visitCol = (col) => {
    if (!col) return;
    for (const w of col.widgets || []) {
      if (!w || w.id === excludeWidgetId) continue;
      if (w.type === 'table-of-contents') continue;
      if (!TOC_SOURCE_TYPES.has(w.type)) continue;
      if (!isIncludedInToc(w.content)) continue;
      const label = resolveTocLabel(w.content, w.type);
      if (!label) continue;
      const pageIndex = pageMap ? (pageMap.get(w.id) ?? null) : null;
      out.push({
        widgetId: w.id,
        sourceType: w.type,
        anchorId: resolveTocAnchorId(w.content, w.id),
        label,
        tocIcon: resolveTocIcon(w.content),
        level: resolveTocLevel(w.content),
        order: order++,
        pageIndex,
        pageNumber: pageIndex != null ? pageIndex + 1 : null,
      });
    }
    for (const nr of col.nestedRows || []) {
      for (const c2 of nr.columns || []) visitCol(c2);
    }
  };

  for (const row of rows || []) {
    for (const col of row.columns || []) visitCol(col);
  }
  return out;
}

/**
 * Mescla entradas detectadas com overrides salvos no widget Sumário.
 * @param {Record<string, unknown>|null|undefined} tocContent
 * @param {TocRegistryEntry[]} availableEntries
 * @returns {Array<TocRegistryEntry & { displayLabel: string, displayLevel: 1|2|3, displayIcon: string }>}
 */
export function buildTocDisplayItems(tocContent, availableEntries) {
  const c = tocContent || {};
  const overrides = Array.isArray(c.items) ? c.items : [];
  const overrideMap = new Map(overrides.map((o) => [o.widgetId, o]));

  /** @type {Array<TocRegistryEntry & { displayLabel: string, displayLevel: 1|2|3, displayIcon: string, sortOrder: number }>} */
  let items = (availableEntries || [])
    .map((entry) => {
      const o = overrideMap.get(entry.widgetId) || {};
      if (o.enabled === false) return null;
      const displayLabel = String(o.label || '').trim() || entry.label;
      const displayLevel = resolveTocLevel(
        { tocLevel: o.level != null ? o.level : entry.level },
        entry.level,
      );
      const displayIcon = String(o.icon || '').trim() || entry.tocIcon || '';
      const sortOrder = o.order != null ? parseInt(o.order, 10) : entry.order;
      return {
        ...entry,
        displayLabel,
        displayLevel,
        displayIcon,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : entry.order,
      };
    })
    .filter(Boolean);

  if (c.sortMode === 'manual') {
    items = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  return items;
}

/**
 * Envolve HTML do elemento-fonte com âncora quando includeInToc (cabeçalho / card).
 * @param {string} innerHtml
 * @param {Record<string, unknown>|null|undefined} content
 * @param {string} widgetId
 * @param {number} [scrollMargin=72]
 * @returns {string}
 */
export function wrapHtmlWithTocAnchorIfNeeded(innerHtml, content, widgetId, scrollMargin = 72) {
  if (!isIncludedInToc(content)) return innerHtml;
  const safeId = resolveTocAnchorId(content, widgetId).replace(/[^a-zA-Z0-9_-]/g, '') || 'ancora';
  const sm = Math.min(240, Math.max(0, parseInt(content?.scrollMarginTop, 10) || scrollMargin));
  return `<div id="${safeId}" class="madri-gc-toc-anchor" style="scroll-margin-top:${sm}px;width:100%;box-sizing:border-box">${innerHtml}</div>`;
}
